import React from 'react';
import { Link } from 'react-router-dom';
import { UploadStatusWidget } from 'src/utils/import';
import { Modal } from 'react-bootstrap';

import { AsyncTask, AsyncTasksTypes } from '../../utils/asyncTasks';
import PagingComponent, { PagedState } from '../../utils/paging';
import AsyncTasksListImportView from '../../utils/AsyncTasksListImportView';
import { RefreshSignaler } from '../../utils/refresher';
import { UploadTaskState } from '../beneficiaries';
import DryRunDialog from './DryRunDialog';
import { Input } from '../../utils/inputs';
import { DownloadStaticFileButton } from '../../utils/DownloadStaticFileButton';
import { SPREADSHEET_EXTENSIONS } from '../../constants/file-upload-extensions.constant';
import { MultipleFilesUpload } from './MultipleFilesMappingUpload';
import { getActiveBeneficiaryMappingTasks } from '../../apiClient';
import BeneficiaryMappingTasksList from './BeneficiaryMappingTasksList';

export interface ListViewProps {
    data: Array<AsyncTask>;
    canAuthorizeTask?: boolean;
    authorizedManagerId: string;
    authorize: (type: string, id: string) => Promise<void>;
    downloadFile: (type: string, id: string) => string;
    canDownload: boolean;
}

export interface AsyncTasksPageStateProps {
    authorizedManagerId?: string;
    name: string;
    type?: string;
    uploadDescription: string;
    exampleLinkTitle: string;
    exampleUploadFile: string;
    tasks?: PagedState<AsyncTask>;
    canPark: boolean;
    canPost: boolean;
    canDownload?: boolean;
    uploadTaskState: UploadTaskState;
    shouldRunDryRun?: boolean;
    shouldShowInlineWidget?: boolean;
    clearUploadTask?(): void;
}

export interface AsyncTasksPageDispatchProps {
    downloadFile: (type: string, id: string) => void;
    loadAsyncTasksList: (page: number, limit: number) => Promise<RefreshSignaler>;
    uploadFile: (data: File, dryRun?: boolean, dryRunAsyncTaskId?: string, autoComplete?: boolean) => Promise<void>;
    authorizeTask: (type: string, id: string) => void;
    listView?: (props: ListViewProps) => React.ReactElement;
    clearUploadTask?(): void;
    onError?: (error: unknown) => void;
}

type AsyncTasksPageProps = AsyncTasksPageStateProps & AsyncTasksPageDispatchProps;

interface AsyncTasksPageState {
    isDryRun: boolean;
    file?: File;
    showMultipleFilesUpload: boolean;
    showConfirmDialog: boolean;
    hasTasksInMapping: boolean;
    validationClosed: boolean;
}

export default class AsyncTasksPage extends React.Component<AsyncTasksPageProps, AsyncTasksPageState> {
    private defaultPageSize = 25;
    private refresher?: RefreshSignaler;

    static defaultProps = {
        listView: AsyncTasksListImportView,
        canDownload: true,
        shouldRunDryRun: false,
    };

    constructor(props: AsyncTasksPageProps) {
        super(props);

        this.defaultPageSize = props.type === AsyncTasksTypes.IMPORT_BENEFICIARIES ? 10 : 25;

        this.state = {
            isDryRun: props.shouldRunDryRun,
            showMultipleFilesUpload: false,
            showConfirmDialog: false,
            hasTasksInMapping: false,
            validationClosed: false,
        };
    }

    async componentDidMount(): Promise<void> {
        this.refresher = await this.props.loadAsyncTasksList(1, this.defaultPageSize);

        // Check for tasks if this is a mapping task
        if (this.props.type === AsyncTasksTypes.IMPORT_BENEFICIARIES_MAPPING) {
            try {
                const response = await getActiveBeneficiaryMappingTasks();
                const hasActiveMappingTasks = response.tasks && response.tasks.length > 0;
                this.setState({
                    hasTasksInMapping: hasActiveMappingTasks,
                    showMultipleFilesUpload: hasActiveMappingTasks, // Automatically open the component if active tasks exist
                });
            } catch (error) {
                if (this.props.onError) {
                    this.props.onError(error);
                }
            }
        }
    }

    componentWillUnmount() {
        if (this.refresher) {
            this.refresher.stop();
        }
        this.props.clearUploadTask?.();
    }

    UNSAFE_componentWillReceiveProps(nextProps: AsyncTasksPageProps) {
        if (!this.state.file) {
            this.setState({
                isDryRun: nextProps.shouldRunDryRun,
            });
        }
    }

    private async changePageRequested(newPage) {
        this.refresher = await this.props.loadAsyncTasksList(newPage, this.defaultPageSize);
    }

    private upload = async (event, dryRun: boolean) => {
        const file = event.target.files[0];
        if (file) {
            this.setState({ file });
            event.target.value = '';

            await this.props.uploadFile(file, dryRun);

            if (!dryRun) {
                this.refresher = await this.props.loadAsyncTasksList(1, this.defaultPageSize);
            }
        }
    };

    private uploadMappingFile = async (file: File, dryRun: boolean, autoComplete?: boolean) => {
        this.setState({ file });
        await this.props.uploadFile(file, dryRun, undefined, autoComplete);

        if (!dryRun) {
            this.refresher = await this.props.loadAsyncTasksList(1, this.defaultPageSize);
        }
    };

    private onDryRunCancel = () => {
        this.setState({ file: undefined });
    };

    private onConfirmDryRun = async (dryRunAsyncTaskId: string, autoComplete?: boolean) => {
        this.setState({ isDryRun: false });
        await this.props.uploadFile(this.state.file, false, dryRunAsyncTaskId, autoComplete);
        this.refresher = await this.props.loadAsyncTasksList(1, this.defaultPageSize);

        // Set validationClosed to true to trigger a re-render of the MultipleFilesUpload component
        // This will cause it to call fetchMappingTasks() again
        this.setState({
            file: undefined,
            validationClosed: true,
        });
    };

    private showConfirmationDialog = () => {
        this.setState({ showConfirmDialog: true });
    };

    private hideConfirmationDialog = () => {
        this.setState({ showConfirmDialog: false });
    };

    private confirmStartNewMapping = () => {
        this.setState({
            showConfirmDialog: false,
            showMultipleFilesUpload: true,
        });
    };

    private toggleMultipleFilesUpload = () => {
        if (this.state.showMultipleFilesUpload) {
            this.setState({ showMultipleFilesUpload: false });
        } else {
            this.showConfirmationDialog();
        }
    };

    private isMappingTask = this.props.type === AsyncTasksTypes.IMPORT_BENEFICIARIES_MAPPING;

    private renderUploadFeature() {
        const {
            name,
            uploadDescription,
            exampleUploadFile,
            exampleLinkTitle,
            uploadTaskState,
            shouldRunDryRun,
            shouldShowInlineWidget,
        } = this.props;
        const { isDryRun, file, showMultipleFilesUpload, showConfirmDialog } = this.state;

        return (
            <div>
                <p>{uploadDescription}</p>
                <p>
                    Please ensure that you have exported this list with the correct structure.{' '}
                    <DownloadStaticFileButton filename={exampleUploadFile}>{exampleLinkTitle}</DownloadStaticFileButton>
                </p>

                <div className="wfp-form--actions">
                    {this.isMappingTask ? (
                        <>
                            {!showMultipleFilesUpload && (
                                <button
                                    className="wfp-btn"
                                    onClick={this.toggleMultipleFilesUpload}
                                    style={{ marginBottom: '20px' }}
                                    type="button"
                                >
                                    Start New Mapping
                                </button>
                            )}

                            {showMultipleFilesUpload && (
                                <MultipleFilesUpload
                                    onClose={this.toggleMultipleFilesUpload}
                                    onError={this.props.onError}
                                    onUpload={(files) => this.uploadMappingFile(files[0], shouldRunDryRun)}
                                    onValidationClose={() => {
                                        // This will be called when the validation section is closed
                                        // We need to refresh the mapping tasks in the MultipleFilesUpload component
                                        // If validationClosed is true, reset it to false
                                        // If validationClosed is false, set it to true
                                        this.setState({ validationClosed: !this.state.validationClosed });
                                    }}
                                    uploadMultipleMappingFiles={(file) => this.uploadMappingFile(file, shouldRunDryRun)}
                                    validationClosed={this.state.validationClosed}
                                />
                            )}
                        </>
                    ) : (
                        <button className="wfp-btn replaced-input-file" type="button">
                            <span>Upload</span>
                            <Input
                                accept={SPREADSHEET_EXTENSIONS}
                                name="data"
                                onChange={(event) => this.upload(event, shouldRunDryRun)}
                                type="file"
                            />
                        </button>
                    )}

                    {isDryRun && !!file && (
                        <DryRunDialog
                            asyncTask={this.props.uploadTaskState.task}
                            downloadFile={this.props.downloadFile}
                            onCancel={this.onDryRunCancel}
                            onConfirm={this.onConfirmDryRun}
                            taskStateError={this.props.uploadTaskState.error}
                            title={name}
                        />
                    )}
                    {shouldShowInlineWidget && !isDryRun && (
                        <UploadStatusWidget
                            importError={uploadTaskState.error}
                            importStatus={uploadTaskState.status}
                            importTask={uploadTaskState.task}
                        />
                    )}
                </div>

                {showConfirmDialog && (
                    <Modal backdrop="static" centered keyboard={false} onHide={this.hideConfirmationDialog} show>
                        <Modal.Header>
                            <Modal.Title>Start New Mapping</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p
                                style={{
                                    display: 'block',
                                    textOverflow: 'ellipsis',
                                    wordWrap: 'break-word',
                                    overflow: 'hidden',
                                }}
                            >
                                Are you sure you want to start a new mapping? This will erase all existing mapping after
                                finishing the process.
                            </p>
                        </Modal.Body>
                        <Modal.Footer>
                            <button
                                className="wfp-btn"
                                onClick={this.hideConfirmationDialog}
                                style={{ marginRight: 10 }}
                                type="button"
                            >
                                No
                            </button>
                            <button className="wfp-btn--primary" onClick={this.confirmStartNewMapping} type="submit">
                                Yes
                            </button>
                        </Modal.Footer>
                    </Modal>
                )}
            </div>
        );
    }

    render() {
        const { canPark, canPost, canDownload, tasks, authorizedManagerId } = this.props;

        return (
            <main>
                <nav className="wfp-breadcrumbs">
                    <ol className="breadcrumbs--wrapper">
                        <li className="breadcrumbs--item">
                            <Link className="breadcrumbs--link" to="/home">
                                <span>Home</span>
                            </Link>
                        </li>
                        <li className="breadcrumbs--item">
                            <Link className="breadcrumbs--link" to="/beneficiaries">
                                <span>Beneficiaries</span>
                            </Link>
                        </li>
                        <li className="breadcrumbs--item">
                            <span className="breadcrumbs--last">{this.props.name}</span>
                        </li>
                    </ol>
                </nav>

                <h3>{this.props.name}</h3>

                {canPark && this.renderUploadFeature()}

                {this.props.tasks && (
                    <div>
                        {this.isMappingTask ? (
                            <BeneficiaryMappingTasksList
                                authorize={this.props.authorizeTask.bind(this)}
                                authorizedManagerId={authorizedManagerId}
                                canAuthorizeTask={canPost}
                                canDownload={canDownload}
                                data={tasks.items}
                                downloadFile={this.props.downloadFile.bind(this)}
                            />
                        ) : (
                            <>
                                <this.props.listView
                                    authorize={this.props.authorizeTask.bind(this)}
                                    authorizedManagerId={authorizedManagerId}
                                    canAuthorizeTask={canPost}
                                    canDownload={canDownload}
                                    data={tasks.items}
                                    downloadFile={this.props.downloadFile.bind(this)}
                                />
                                <PagingComponent
                                    onPageChanged={this.changePageRequested.bind(this)}
                                    paging={tasks.paging}
                                />
                            </>
                        )}
                    </div>
                )}
            </main>
        );
    }
}
